import type _jquery from "jquery";
import { type DeepReadonly, type Ref, ref, readonly, provide } from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord,
} from "@mcwd/util-vue";
import { exhaustiveGuard } from "@mcwd/typescript-type-guards";
import { MarketoFormState } from "../types/definitions/marketo-form-state.js";
import { useGatingLevel } from "./use-gating-level.js";
import { useFormEvents } from "./use-form-events.js";
import { useFormLogger } from "./use-form-logger.js";

export const SetFormStateValue = [
  "BeforeLoad",
  "Loading",
  "Setup",
  "Ready",
  "Complete"
] as const satisfies readonly (Exclude<MarketoFormState, "RegularForm" | "BlindForm" | "RegularFormComplete"> | "Ready" | "Complete")[];

export type SetFormStateValue = (typeof SetFormStateValue)[number];


type UseFormStateInjectionValues = InjectionValuesRecord<{
  FormState: DeepReadonly<Ref<MarketoFormState>>;
  setFormState(value: SetFormStateValue): Promise<void>;
}>;

type UseFormStateInjectionKeys =
  InjectionKeysRecord<UseFormStateInjectionValues>;

const UseFormStateInjectionKeys = CreateInjectionKeysRecord<UseFormStateInjectionKeys>({
  FormState: "FormState",
  setFormState: "setFormState",
});

function getValidBeforeStates(state: MarketoFormState): MarketoFormState[] {
  switch (state) {
    case "BeforeLoad": return ["BeforeLoad"];
    case "Loading": return ["BeforeLoad"];
    case "Setup": return ["Loading"];
    case "RegularForm": return ["Setup"];
    case "BlindForm": return ["Setup", "RegularForm", "BlindForm"];
    case "RegularFormComplete": return ["RegularForm"];
    default: exhaustiveGuard(state);
  }
}


export function initFormState() {
  if (checkValuesAlreadyProvided(UseFormStateInjectionKeys)) {
    return;
  }
  const FormState = ref<MarketoFormState>("BeforeLoad");
  const { CurrentMarketoFormGatingState } = useGatingLevel({callerLabel: "initFormState"});
  const events = useFormEvents({ callerLabel: "initFormState" });
  async function setFormState(value: SetFormStateValue) {
    if (value === "Ready") {
      await events.ready.trigger({ eventName: "ready" });
    }
    else if (value === "Complete") {
      await events.completed.trigger({ eventName: "completed", gatingState: CurrentMarketoFormGatingState.value });
    }
    else if (value === "BeforeLoad" || value === "Loading" || value === "Setup") {
      // Do nothing.
    }
    else {
      exhaustiveGuard(value);
    }
    let state: MarketoFormState;
    // We transform "Ready" and "Complete" into more specific values depending on the Gating State
    
    if (CurrentMarketoFormGatingState.value === "BlindSubmit") {
      // The blind form is never complete, since a user could click multiple downloads
      if (value === "Ready" || value === "Complete") {
        state = "BlindForm";
      }
      else {
        state = value;
      }
    }
    else if (CurrentMarketoFormGatingState.value === "FullGate" || CurrentMarketoFormGatingState.value === "PartialGate") {
      if (value === "Ready") {
        state = "RegularForm";
      }
      else if (value === "Complete") {
        state = "RegularFormComplete";
      }
      else {
        state = value;
      }
    }
    else {
      exhaustiveGuard(CurrentMarketoFormGatingState.value);
    }

    const validStates = getValidBeforeStates(state);
    if (validStates.includes(FormState.value)) {
      FormState.value = state;
    } else {
      throw new Error(
        `Invalid Form State "${state}" following "${FormState.value}"`
      );
    }
  }
  provide(UseFormStateInjectionKeys.FormState, readonly(FormState));
  provide(UseFormStateInjectionKeys.setFormState, setFormState);
}

export function useFormState ({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useFormState";
    const FormState = injectRequiredWithSelf(UseFormStateInjectionKeys.FormState);
    const setFormState = injectRequiredWithSelf(UseFormStateInjectionKeys.setFormState);
    return {
      FormState,
      // Create a new function to wrap the original one and pass the currentGatingState value into the second param.
      setFormState
    };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}
